import saveAs from 'file-saver';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { objectUrlToBlob } from '../../utils';

import {
  ConverterStatus,
  IAppFile,
  IConverterBase,
  IConverterOptions,
  IProgress,
  ValidateFn,
} from './types';

function useConverterBase<O extends IConverterOptions, R extends IAppFile>(
  defaultOptions: O | (() => O),
  validateFn: ValidateFn,
): IConverterBase<O, R> {
  const [sources, setSources] = useState<IAppFile[]>([]);
  const [options, setOptions] = useState<O>(defaultOptions);

  const [status, setStatus] = useState<ConverterStatus>(ConverterStatus.INIT);
  const [progress, setProgress] = useState<IProgress>();
  const [results, setResults] = useState<R[]>([]);

  const [actionsDisabled, setActionsDisabled] = useState<boolean>(false);
  const [error, setInnerError] = useState<string>(null);

  useEffect(() => {
    const disable =
      status === ConverterStatus.PROCESSING ||
      status === ConverterStatus.CONVERTING ||
      status === ConverterStatus.DOWNLOADING;

    setActionsDisabled(disable);
  }, [status]);

  const reset = useCallback(() => {
    sources.forEach((s) => URL.revokeObjectURL(s.url));
    results.forEach((s) => URL.revokeObjectURL(s.url));

    setOptions(defaultOptions);
    setStatus(ConverterStatus.INIT);
    setProgress(undefined);
    setSources([]);
    setResults([]);
  }, [defaultOptions, results, sources]);

  const is = useCallback(
    (...sts: ConverterStatus[]) => {
      return sts.includes(status);
    },
    [status],
  );

  const removeAt = useCallback(
    (index: number) => {
      if (status >= ConverterStatus.CONVERTED) {
        setResults((prev) => {
          const newResults = prev.filter((result, i) => {
            if (i === index) {
              URL.revokeObjectURL(result.url);
            }

            return i !== index;
          });

          if (newResults.length === 0) {
            setStatus(ConverterStatus.EMPTY);
          }

          return newResults;
        });
      }

      setSources((prev) => {
        const newSources = prev.filter((source, i) => {
          if (i === index) {
            URL.revokeObjectURL(source.url);
          }

          return i !== index;
        });

        if (newSources.length === 0) {
          setStatus(ConverterStatus.EMPTY);
        }

        return newSources;
      });
    },
    [status],
  );

  const removeAll = useCallback(() => {
    setOptions(defaultOptions);
    setStatus(ConverterStatus.EMPTY);
    setProgress(undefined);
    setSources([]);
    setResults([]);
  }, [defaultOptions]);

  const setError = useCallback((e: string, converterStatus?: ConverterStatus) => {
    setProgress(undefined);
    setResults([]);
    setStatus(converterStatus || ConverterStatus.ERROR);
    setInnerError(e);
  }, []);

  const download = useCallback(async () => {
    setStatus(ConverterStatus.DOWNLOADING);

    try {
      if (results.length <= 10) {
        for (const result of results) {
          if (result instanceof Blob) {
            saveAs(result, sources[0].fileName);
          } else {
            saveAs(await objectUrlToBlob(result.url), result.fileName);
          }
        }
      } else {
        const jsZip = await import('jszip');
        const zip = jsZip.default();

        for (const result of results) {
          if (result instanceof Blob) {
            saveAs(result, sources[0].fileName);
          } else {
            saveAs(await objectUrlToBlob(result.url), result.fileName);
          }
        }
        const zipData = await zip.generateAsync({ type: 'blob' });
        saveAs(zipData, 'image_resize_files.zip');
      }

      setStatus(ConverterStatus.DOWNLOADED);
    } catch (e) {
      setError(e);
    }
  }, [results, setError, sources]);

  const validateAndSetSources = useCallback(
    async (newSources: IAppFile[] | ((prev: IAppFile[]) => IAppFile[])) => {
      setStatus(ConverterStatus.PROCESSING);

      const src = typeof newSources === 'function' ? newSources(sources) : newSources;

      const validationError = await validateFn?.(src);

      if (validationError) {
        setError(validationError, ConverterStatus.UPLOAD_ERROR);
        return;
      }

      setSources(src);
      setStatus(ConverterStatus.PROCESSED);
    },
    [setError, sources, validateFn],
  );

  return useMemo(
    () => ({
      status,
      options,
      progress,
      sources,
      results,
      actionsDisabled,
      error,

      setOptions,
      setSources: validateAndSetSources,
      setSourcesWithoutValidation: setSources,
      removeAt,
      removeAll,
      reset,
      is,
      download,

      setStatus,
      setProgress,
      setResults,

      setError,
    }),
    [
      status,
      options,
      progress,
      sources,
      results,
      actionsDisabled,
      error,
      setError,
      validateAndSetSources,
      removeAt,
      removeAll,
      reset,
      is,
      download,
    ],
  );
}

export default useConverterBase;
